import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MapPin, Clock, Briefcase, Upload, CheckCircle2, AlertCircle } from "lucide-react";
import { api, API } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { InnerBlock } from "@/components/InnerBlock";
import NotFound from "@/pages/NotFound";

const inputCls = "h-11 w-full rounded-md border border-wafa-deep/15 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-wafa-deep";
const MAX_CV = 5 * 1024 * 1024;

export default function JobDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | notfound

  useEffect(() => {
    api.get(`/jobs/${slug}`)
      .then(({ data }) => { setJob(data); setStatus("ready"); })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading") return <div className="mx-auto max-w-7xl px-4 py-24 text-sm text-wafa-ink/60">Loading…</div>;
  if (status === "notfound" || !job) return <NotFound />;

  const posted = job.posted_at ? new Date(job.posted_at).toISOString() : new Date().toISOString();
  const jobPostingSchema = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: job.description || job.summary,
    datePosted: posted,
    validThrough: job.closes_at || undefined,
    employmentType: (job.employment_type || "").toUpperCase().replace("-", "_"),
    hiringOrganization: {
      "@type": "Organization",
      name: "Wafa Solutions LLC",
      sameAs: "https://wafa.om",
    },
    jobLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: job.location,
        addressCountry: "OM",
      },
    },
    experienceRequirements: job.experience_level,
  };

  return (
    <div data-testid={`job-detail-${job.slug}`}>
      <Seo title={`${job.title} — Careers at Wafa Solutions`} description={job.summary} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jobPostingSchema) }} />

      <PageHero
        eyebrow={`Careers · ${job.department}`}
        h1={job.title}
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Careers", to: "/company/careers" }, { label: job.title }]}
      />

      <InnerBlock tint="white">
        <Link to="/company/careers" className="mb-6 inline-flex items-center gap-1 text-sm text-wafa-ink/70 hover:text-wafa-deep" data-testid="job-detail-back">
          <ArrowLeft className="h-4 w-4" /> All roles
        </Link>

        <div className="flex flex-wrap items-center gap-4 text-sm text-wafa-ink/75">
          <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{job.location}</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" />{job.employment_type}</span>
          <span className="inline-flex items-center gap-1.5"><Briefcase className="h-4 w-4" />{job.experience_level}</span>
        </div>

        <p className="mt-8 max-w-3xl text-base leading-relaxed text-wafa-ink/85 lg:text-lg" data-testid="job-summary">{job.summary}</p>

        {job.description ? (
          <div className="mt-8 max-w-3xl whitespace-pre-line text-base leading-relaxed text-wafa-ink/85 lg:text-lg" data-testid="job-description">
            {job.description}
          </div>
        ) : null}

        <Section title="What you'll do" items={job.responsibilities} testid="job-responsibilities" />
        <Section title="What we're looking for" items={job.requirements} testid="job-requirements" />
        <Section title="Nice to have" items={job.nice_to_have} testid="job-nice-to-have" />
      </InnerBlock>

      <InnerBlock tint="cream" heading="Apply for this role" eyebrow="Application">
        <ApplyForm slug={job.slug} />
      </InnerBlock>
    </div>
  );
}

function Section({ title, items, testid }) {
  if (!items?.length) return null;
  return (
    <div className="mt-10 max-w-3xl" data-testid={testid}>
      <h3 className="font-heading text-xl font-semibold text-wafa-deep sm:text-2xl">{title}</h3>
      <ul className="mt-4 space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-wafa-ink/85 lg:text-base">
            <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-wafa-primary" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ApplyForm({ slug }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [portfolio, setPortfolio] = useState("");
  const [cover, setCover] = useState("");
  const [cv, setCv] = useState(null);
  const [err, setErr] = useState("");
  const [state, setState] = useState("idle");

  const onPickCv = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["pdf", "docx"].includes(ext)) { setErr("CV must be a PDF or DOCX file."); setCv(null); return; }
    if (f.size > MAX_CV) { setErr("CV must be under 5 MB."); setCv(null); return; }
    setErr("");
    setCv(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!name.trim()) return setErr("Please enter your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setErr("Please enter a valid email.");
    if (!cv) return setErr("Please attach your CV (PDF or DOCX, max 5 MB).");
    const data = new FormData();
    data.append("name", name.trim());
    data.append("email", email.trim().toLowerCase());
    data.append("phone", phone.trim());
    data.append("portfolio_url", portfolio.trim());
    data.append("cover_note", cover.trim());
    data.append("cv", cv);
    setState("sending");
    try {
      await axios.post(`${API}/jobs/${slug}/apply`, data);
      setState("sent");
    } catch (e2) {
      setState("idle");
      setErr(e2?.response?.data?.detail || "Application could not be submitted.");
    }
  };

  if (state === "sent") return (
    <div className="rounded-lg border border-wafa-primary/30 bg-white p-8 text-center" data-testid="apply-success">
      <CheckCircle2 className="mx-auto h-10 w-10 text-wafa-primary" />
      <h3 className="mt-3 font-heading text-xl font-semibold text-wafa-deep">Thanks — application received.</h3>
      <p className="mt-2 text-sm text-wafa-ink/75">We&apos;ll review your CV and be in touch soon.</p>
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="apply-form" className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      <div><label className={labelCls}>Full name *</label><input data-testid="apply-name" className={inputCls + " mt-2"} value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div><label className={labelCls}>Email *</label><input type="email" data-testid="apply-email" className={inputCls + " mt-2"} value={email} onChange={(e) => setEmail(e.target.value)} /></div>
      <div><label className={labelCls}>Phone</label><input data-testid="apply-phone" className={inputCls + " mt-2"} value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
      <div><label className={labelCls}>LinkedIn / portfolio URL</label><input data-testid="apply-portfolio" className={inputCls + " mt-2"} value={portfolio} onChange={(e) => setPortfolio(e.target.value)} /></div>
      <div className="sm:col-span-2">
        <label className={labelCls}>CV * (PDF or DOCX, max 5 MB)</label>
        <div className="mt-2 flex items-center gap-3">
          <input type="file" id="apply-cv-input" accept=".pdf,.docx" data-testid="apply-cv" onChange={onPickCv} className="hidden" />
          <label htmlFor="apply-cv-input" className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-wafa-deep/15 bg-white px-4 py-2 text-sm font-medium text-wafa-deep hover:border-wafa-deep">
            <Upload className="h-4 w-4" /> Choose file
          </label>
          {cv ? <span className="text-xs text-wafa-ink/70">{cv.name} · {(cv.size / 1024).toFixed(0)} KB</span> : null}
        </div>
      </div>
      <div className="sm:col-span-2"><label className={labelCls}>Cover note</label><textarea data-testid="apply-cover" rows={4} className="mt-2 w-full rounded-md border border-wafa-deep/15 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" value={cover} onChange={(e) => setCover(e.target.value)} /></div>
      {err ? <div className="sm:col-span-2 flex items-center gap-2 text-sm text-wafa-tomato" data-testid="apply-error"><AlertCircle className="h-4 w-4" /> {err}</div> : null}
      <div className="sm:col-span-2">
        <button type="submit" disabled={state === "sending"} data-testid="apply-submit" className="inline-flex items-center justify-center gap-2 rounded-md bg-wafa-primary px-6 py-3 text-sm font-semibold text-white hover:bg-wafa-deep disabled:opacity-60">
          {state === "sending" ? "Sending…" : "Submit application"}
        </button>
      </div>
    </form>
  );
}
